import resumeData from "../../data/resumeData";
import { tokenize } from "./tokenize";
import { BM25Index } from "./bm25";

const pushChunk = (chunks, { id, source, title, text }) => {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (!cleaned) return;
  chunks.push({
    id,
    source,
    title,
    text: cleaned,
    tokens: tokenize(`${title} ${cleaned}`),
  });
};

export const buildPortfolioChunks = (data = resumeData) => {
  const chunks = [];

  pushChunk(chunks, {
    id: "profile-intro",
    source: "Profile",
    title: `${data.name} — positioning`,
    text: `${data.title}. ${data.positioning} ${data.introduction} Based in ${data.contact.location}.`,
  });

  data.expertise.forEach((area, index) => {
    pushChunk(chunks, {
      id: `expertise-${index}`,
      source: "Expertise",
      title: area.title,
      text: `${area.title} skills include: ${area.items.join(", ")}.`,
    });
  });

  data.aiFocus.forEach((item, index) => {
    pushChunk(chunks, {
      id: `ai-focus-${index}`,
      source: "AI Focus",
      title: item.title,
      text: `${item.status}. ${item.description} Tools: ${item.tools.join(", ")}.`,
    });
  });

  data.industries.forEach((industry, index) => {
    pushChunk(chunks, {
      id: `industry-${index}`,
      source: "Industries",
      title: industry.name,
      text: `Industry experience in ${industry.name} with clients: ${industry.clients.join(", ")}.`,
    });
  });

  data.experience.forEach((role, index) => {
    const clientPart = role.client ? ` for client ${role.client}` : "";
    pushChunk(chunks, {
      id: `experience-${index}`,
      source: "Experience",
      title: `${role.role} · ${role.company}${role.client ? ` / ${role.client}` : ""}`,
      text: `${role.period}. ${role.role} at ${role.company}${clientPart} in ${role.location}. ${role.highlights.join(" ")}`,
    });
  });

  data.projects.forEach((project, index) => {
    pushChunk(chunks, {
      id: `project-${index}`,
      source: "Projects",
      title: project.name,
      text: `${project.category}. ${project.description} ${project.impact || ""} Tools: ${project.tools.join(", ")}.`,
    });
  });

  (data.earlierProjects || []).forEach((project, index) => {
    pushChunk(chunks, {
      id: `earlier-project-${index}`,
      source: "Projects",
      title: project.name,
      text: project.summary,
    });
  });

  data.certifications.forEach((cert, index) => {
    pushChunk(chunks, {
      id: `cert-${index}`,
      source: "Certifications",
      title: cert.name,
      text: `${cert.name} (${cert.code}, ${cert.year}).`,
    });
  });

  pushChunk(chunks, {
    id: "education",
    source: "Education",
    title: data.education.degree,
    text: `${data.education.degree} at ${data.education.institution}, ${data.education.period}. Result: ${data.education.result}. Languages: ${data.languages}.`,
  });

  return chunks;
};

let cachedIndex = null;
let cachedChunkCount = 0;

export const getPortfolioRagIndex = () => {
  if (!cachedIndex) {
    const chunks = buildPortfolioChunks();
    cachedChunkCount = chunks.length;
    cachedIndex = new BM25Index(chunks);
  }
  return cachedIndex;
};

export const getPortfolioChunkCount = () => {
  getPortfolioRagIndex();
  return cachedChunkCount;
};

export const SAMPLE_RAG_QUERIES = [
  "What RAG or LangChain work have you done?",
  "How did you handle security at Allianz?",
  "Tell me about AI agents and Flowise",
  "What cloud and Kubernetes experience do you have?",
  "BayWa support and AI-assisted triage",
];

export const assembleContextPrompt = (question, hits) => {
  const contextBlocks = hits
    .map(
      (hit, index) =>
        `[${index + 1}] (${hit.source} · ${hit.title})\n${hit.text}`
    )
    .join("\n\n");

  return [
    "System: Answer only using the retrieved portfolio context. If the context is insufficient, say what is missing.",
    "",
    "Retrieved context:",
    contextBlocks || "(no chunks retrieved)",
    "",
    `User question: ${question}`,
  ].join("\n");
};
